import { useCallback, useMemo } from 'react'
import { PayloadIn } from '../xmpp-client/types'
import { Affiliations, Context, Marker, ParsedMarker, ParsedPayloadIn, Setters } from './types'
import { useImmer } from './useImmer'

type Event = PayloadIn | Marker | Affiliations

export const useChatDataState = () => {
  const [{ events }, setState] = useImmer<Context>({
    events: {},
  })

  const getMergedEvent = useCallback(
    (oldEvent: Event | undefined, newEvent: Event): ParsedPayloadIn | ParsedMarker | undefined => {
      if (newEvent.type === 'Affiliations' || !newEvent.id) {
        return undefined
      }
      return {
        ...newEvent,
        id: newEvent.id,
        timestamp: newEvent.timestamp ?? oldEvent?.timestamp ?? new Date().toISOString(),
      }
    },
    []
  )

  const setEvents: Setters['setEvents'] = useCallback(
    (roomId, events, callback) => {
      setState(draft => {
        const roomEvents = draft.events[roomId] ?? {}
        const mergedEvents = events
          .map(event => {
            if (event.type === 'Affiliations' || !event.id) {
              return undefined
            }
            return getMergedEvent(roomEvents[event.id], event)
          })
          .filter(event => event !== undefined)
        mergedEvents.forEach(newEvent => {
          if (!newEvent) {
            return
          }
          roomEvents[newEvent.id] = newEvent
        })
        draft.events[roomId] = roomEvents
        callback?.(mergedEvents)
        return draft
      })
    },
    [getMergedEvent, setState]
  )

  const setEvent: Setters['setEvent'] = useCallback(
    (event, callback) => {
      setEvents(event.roomId, [event], events => {
        if (events[0]) {
          return callback?.(events[0])
        }
        return events
      })
    },
    [setEvents]
  )

  const setters = useMemo(
    () => ({
      setEvent,
      setEvents,
    }),
    [setEvent, setEvents]
  )

  const value = useMemo(
    () => ({
      events,
    }),
    [events]
  )

  return { value, setters }
}
